import React, { useCallback, useEffect, useState } from 'react';
import type { Npc } from '../types';
import { createNpc, deleteNpc, fetchNpcs, updateNpc } from '../api/npcs';
import { NpcModal } from './NpcModal';
import { ConfirmDialog } from './ConfirmDialog';
import { AdminExportImportBar } from './AdminExportImportBar';
import { barPercent } from '../utils/avatars';
import { NpcMark } from './Pictogram';

export function NpcManagerSection() {
  const [npcs, setNpcs] = useState<Npc[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Npc | null | undefined>(undefined);
  const [toDelete, setToDelete] = useState<Npc | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setNpcs(await fetchNpcs());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Gegner konnten nicht geladen werden.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="data-section">
      <div className="section-header">
        <h2>Gegner-Verwaltung</h2>
        <div className="section-header-actions">
          <AdminExportImportBar onImported={() => void load()} />
          <button type="button" className="medieval-button" onClick={() => setEditing(null)}>
            + Gegner hinzufügen
          </button>
        </div>
      </div>
      <p className="section-info">
        Vorlagen — Katalog exportieren/importieren inklusive Gegner. In der Spielübersicht zuordnen; Spieler sehen sie nicht.
      </p>
      {loadError && <p className="form-error" role="alert">{loadError}</p>}
      <div className="data-grid">
        {npcs.map(npc => (
          <article key={npc.id} className="data-card npc-card combat-card npc">
            <div className="combat-card-avatar" aria-hidden>
              <NpcMark icon={npc.icon} iconUrl={npc.icon_url} npcType={npc.npc_type} />
            </div>
            <div className="combat-card-body">
            <header>
              <strong>{npc.name}</strong>
              {npc.npc_type && <span className="combat-card-class">{npc.npc_type}</span>}
            </header>
            <div className="combat-bar combat-hp">
              <div className="combat-bar-fill combat-hp-fill" style={{ width: `${barPercent(npc.health, npc.max_health)}%` }} />
              <span>HP {npc.health}/{npc.max_health}</span>
            </div>
            <div className="combat-stats">
              <span className="combat-stat">⚔ {npc.damage}</span>
              <span className="combat-stat">➤ {npc.movement}</span>
            </div>
            {npc.abilities && npc.abilities.length > 0 && (
              <p className="combat-kit">Fähigkeiten: {npc.abilities.map(a => a.name).join(', ')}</p>
            )}
            <p className="section-info">Skalierung: {scalingLabel(npc)}</p>
            <div className="card-actions">
              <button type="button" className="medieval-button edit-btn" onClick={() => setEditing(npc)}>
                ✏️ Bearbeiten
              </button>
              <button type="button" className="delete-button" onClick={() => setToDelete(npc)}>
                Löschen
              </button>
            </div>
            </div>
          </article>
        ))}
      </div>
      {npcs.length === 0 && !loadError && (
        <p style={{ textAlign: 'center', color: '#ccc', padding: '20px' }}>
          Noch keine Gegner. Lege die erste Vorlage an und füge sie danach in einem Spiel hinzu.
        </p>
      )}
      {editing !== undefined && (
        <NpcModal
          initial={editing}
          onClose={() => setEditing(undefined)}
          onSubmit={async payload => {
            if (editing) {
              await updateNpc(editing.id, payload);
            } else {
              await createNpc(payload);
            }
            setEditing(undefined);
            await load();
          }}
        />
      )}
      {toDelete && (
        <ConfirmDialog
          title="Gegner löschen?"
          message={`Vorlage „${toDelete.name}“ löschen? Bereits einem Spiel zugeordnete Kopien bleiben dort erhalten.`}
          confirmLabel="Löschen"
          danger
          onCancel={() => setToDelete(null)}
          onConfirm={() => {
            void deleteNpc(toDelete.id)
              .then(() => load())
              .finally(() => setToDelete(null));
          }}
        />
      )}
    </div>
  );
}

function scalingLabel(npc: Npc) {
  if (npc.scaling_mode === 'per_stat') {
    const basisLabel: Record<string, string> = {
      level: 'Level',
      player_count: 'Spieler',
      combat_foes: 'Helden',
      combat_heroes: 'Helden',
    };
    const statLabel: Record<string, string> = {
      health: 'Leben',
      damage: 'Schaden',
      movement: 'Bewegung',
    };
    const parts = (['health', 'damage', 'movement'] as const).flatMap(stat => {
      const names = Array.from(new Set(Object.keys(npc.stat_scaling?.[stat] || {}).map(key => basisLabel[key]).filter(Boolean)));
      return names.length ? [`${statLabel[stat]} nach ${names.join(' und ')}`] : [];
    });
    return parts.join(', ') || 'keine';
  }
  if (npc.scaling_mode === 'level') return 'nach Level';
  if (npc.scaling_mode === 'player_count') return 'nach Anzahl Spieler';
  if (npc.scaling_mode === 'combat_heroes') return 'nach Helden im Kampf';
  if (npc.scaling_mode === 'combat_foes') return 'nach Gegnern im Kampf';
  if (npc.scaling_mode === 'formula') return 'eigene Formel';
  return 'keine';
}
